import { useEffect, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import cn from 'classnames';
import {
  useTasks,
  useResolverUrl,
  useNewTasks,
  useTaskActionData,
} from '../utils/hooks';
import { CheckIcon, RobotIcon } from '../../../components';
import { TaskRef, TaskStatus } from '../utils/types';

interface SidebarItemProps {
  task: TaskRef;
}

const SidebarItem = ({ task: { id, status, title } }: SidebarItemProps) => {
  const ref = useRef<HTMLLIElement>(null);
  const { taskId } = useParams();
  const urlPrefix = useResolverUrl();
  const newTasks = useNewTasks();
  const isCurrentTask = taskId === id;
  const isTodo = status === TaskStatus.Todo;
  const isDone = status === TaskStatus.Done;
  const isInProgress = status === TaskStatus.InProgress;
  const isNew = newTasks.find((newTask) => newTask.id === id);

  useEffect(() => {
    if (isCurrentTask && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isCurrentTask]);

  return (
    <li
      ref={ref}
      key={id}
      className={cn('group relative flex gap-x-1 rounded-md pl-2', {
        'bg-newTeal-200 hover:bg-newTeal-300': isCurrentTask && !isNew,
        'hover:bg-newTeal-100': !isCurrentTask && !isNew,
        'hover:bg-teal-400': !isCurrentTask && isNew,
      })}
    >
      {isNew && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 animate-pulse rounded-md border border-teal-500 group-hover:border-teal-550"
        ></div>
      )}

      <div
        aria-hidden="true"
        className={cn(
          'my-2 flex size-4 shrink-0 items-center justify-center rounded-full border text-white',
          {
            'border-newTeal-300 bg-rose-50': isTodo,
            'border-newTeal-500 bg-newTeal-500': isDone,
            'border-indigo-600 bg-indigo-600': isInProgress,
          }
        )}
      >
        {isDone && <CheckIcon className="size-3" strokeWidth={4} />}
        {isInProgress && <RobotIcon className="-mt-0.5 size-3" />}
      </div>

      <NavLink
        to={`${urlPrefix}/task/${id}`}
        className="w-full p-2 text-newTeal-700 hover:text-newTeal-800"
        aria-label={`${title}${isDone ? ' (Complete)' : ''}`}
      >
        {title}
      </NavLink>
    </li>
  );
};

export const ResolverSidebar = () => {
  const allTasks = useTasks();
  const actionData = useTaskActionData();
  const newTasks = useNewTasks();
  const [hideNewTasks, setHideNewTasks] = useState(true);
  const tasks = allTasks.filter(({ id }) => {
    if (hideNewTasks && newTasks.find((newTask) => newTask.id === id)) {
      return false;
    }

    return true;
  });

  useEffect(() => {
    if (actionData?.resolved) {
      setHideNewTasks(true);
      setTimeout(setHideNewTasks, 3000, false);
    }
  }, [actionData]);

  return (
    <nav className="p-6" aria-live="polite" aria-relevant="additions">
      <h2 className="mb-4 text-xs font-medium uppercase tracking-widest text-zinc-600">
        Tasks
      </h2>

      <ul className="grow space-y-1 text-xs">
        {tasks.map((task) => (
          <SidebarItem key={task.id} task={task} />
        ))}
      </ul>
    </nav>
  );
};
